import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import FormTypes from 'common/Forms/FormTypes';

import FormWorkflowStages from 'common/Workflows/FormWorkflowStages';
import TinCheckStatus from 'components/TinCheckStatus';
import SupportingDocumentCures from 'components/Common/SupportingDocumentsCures';

const MANUAL_REJECTION_MIN_CODE = 970001;
const MANUAL_REJECTION_MAX_CODE = 970098;

export function buildDescription({ codeDescription, context }) {
  if (typeof codeDescription !== 'string' || codeDescription === '') {
    return '';
  }

  // check description for dynamic parts like ${...}
  const interpolationParts = codeDescription.match(/\${[^}\r\n]*}/g);
  if (interpolationParts && interpolationParts.length) {
    let result = codeDescription;
    interpolationParts.forEach((template) => {
      if (template != null) {
        const matches = template.match(/\${([^}]*)}/);
        const contextField = matches != null && matches[1] != null;
        if (contextField != null) {
          result = result.split(template).join(context[contextField]);
        }
      }
    });
    return result;
  }
  return codeDescription;
}

function isOverridden(overrides, code) {
  return overrides != null && overrides.current != null && overrides.current.rejections != null && overrides.current.rejections.filter(r => r.code === code).length > 0;
}

function isValid(calculateValidity, rejections, overrides, valid) {
  if (calculateValidity
    && overrides != null
    && overrides.current != null) {
    if (overrides.current.formOverride != null && overrides.current.formOverride.override) {
      return overrides.current.formOverride.valid;
    }

    if (overrides.current.rejections != null && overrides.current.rejections.length > 0) {
      const overrideCodes = overrides.current.rejections.map(a => a != null && a.code);
      return rejections.filter(item => item != null && item.code !== 'REJ500000' && overrideCodes.indexOf(item.code) === -1).length === 0;
    }
  }
  return valid;
}

function hasOverriddenRejection(code, rejections, overrides) {
  if (rejections != null && overrides != null && overrides.current != null && overrides.current.rejections != null) {
    const numericCode = code.replace('REC', '').replace('WRN', '');
    const isInRejections = rejections.filter(item => item.code.replace('REJ', '') === numericCode).length !== 0;
    const isInOverrides = overrides.current.rejections.filter(item => item.code.replace('REJ', '') === numericCode).length !== 0;

    return isInRejections && isInOverrides;
  }
  return false;
}

function overriddenToInvalid(calculateValidity, overrides) {
  return overrides != null
  && overrides.current != null
  && overrides.current.formOverride != null
  && overrides.current.formOverride.override
  && ((!calculateValidity && !overrides.current.formOverride.isDraft) || (calculateValidity && overrides.current.formOverride.isDraft))
  && !overrides.current.formOverride.valid;
}

function overriddenToInvalidText(overrides) {
  return (overrides
    && overrides.current != null
    && overrides.current.formOverride != null
    && overrides.current.formOverride.override)
    ? overrides.current.formOverride.comments
    : '';
}

function validityDisplayText(valid, treaty, locked, workflowStage, formFamily) {
  let statusText = `${ formFamily }: `;
  statusText += valid ? 'Valid' : 'Invalid';
  statusText += valid && treaty ? ' with Treaty' : '';
  statusText += valid && workflowStage === FormWorkflowStages.Complete ? ' (Not Expired)' : '';
  statusText += locked ? ' & Locked' : '';

  return statusText;
}

function isExpired(expiryDate) {
  if (!expiryDate) return false;

  const formatedDate = moment(expiryDate).format('YYYY MM DD');
  const m = moment();
  m.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  return m.diff(formatedDate) > 0;
}

function expiryDisplayText(expiryDate, expiryReason, formFamily) {
  return <div>
    <h2 className="invalid">
      { formFamily }: Expired on { moment(expiryDate).format('ll') }
      <p className="sub-text">Expiration due to { expiryReason }</p>
    </h2>
  </div>;
}

function getDisplayTextForName(formType) {
  if (formType.indexOf('CRSCP') === -1) {
    return '| Name:';
  }
  return '| Controlling Person Name:';
}

function isPiiValidationCode(formType, piiValidationCodes, validationCode) {
  const formTypeFamily = formType && FormTypes[formType.toLowerCase()];
  const family = formTypeFamily && formTypeFamily.family && formTypeFamily.family.toUpperCase();

  const result = family && piiValidationCodes && piiValidationCodes[family]
  && piiValidationCodes[family].filter(code => code.substring(4) === validationCode.substring(3)).length !== 0;
  return result;
}

function isPiiInstruction(formType, piiValidationCodes, instruction, warnings, rejections) {
  if (instruction.indexOf('REC') === -1) {
    return false;
  }

  warnings = warnings.filter(w => w.code.indexOf('REC') === -1);

  const numericCode = instruction.replace('REC', '');
  const validationCodes = [...warnings, ...rejections].filter(v => v.code.indexOf(numericCode) !== -1);

  const result = validationCodes
  && validationCodes.filter(validationCode => isPiiValidationCode(formType, piiValidationCodes, validationCode && validationCode.code) === true).length !== 0;
  return result;
}

const PiiValidationResults = ({
  isOverriddenToInvalid,
  overrides,
  validationCodes,
  headerName,
  formType,
  piiValidationCodes,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const className = headerName === 'Rejections' ? 'reasons-for-rejection-list' : 'red-flags-and-recs-list';

  return (
    <div className="pii-restricted-results">
      {(validationCodes && validationCodes.length !== 0)
      && validationCodes.some(validationCode => isPiiValidationCode(formType, piiValidationCodes, validationCode.code))
      && <button type="button" className="pii-codes-toggle" onClick={ () => setIsVisible(!isVisible) }>
        <span className={ `icon icon-arrow-${ isVisible ? 'down' : 'right' }` } />
        Personally Identifiable Information (PII) { headerName }:
      </button> }
      { isVisible && <div className={ className }>
        { headerName === 'Rejections' && <ul>
          { isOverriddenToInvalid === true && <li>
            <em>Client specific treatment, specified below.</em>
            &nbsp;(<strong>REJ999999</strong>)
          </li> }
          { validationCodes.map(rejection => rejection != null && isPiiValidationCode(formType, piiValidationCodes, rejection.code) && (
          <li key={ rejection.key } className={ isOverridden(overrides, rejection.code) ? 'disabled' : '' }>
            <em>{ buildDescription(rejection) }</em>
          &nbsp;(<strong>{ rejection.code }</strong>)
          </li>
          )) }
        </ul>}
        { headerName === 'Warnings' && <ul>
          { isOverriddenToInvalid && <li className="red-flag">
            <em>{ overriddenToInvalidText(overrides) }</em>
              &nbsp;(<strong>WRN999999</strong>)
            </li> }
          {validationCodes.map(warning => warning != null && isPiiValidationCode(formType, piiValidationCodes, warning.code) && (
          <li
            key={ warning.key }
            className={ `red-flag ${ hasOverriddenRejection(warning.code, validationCodes, overrides) ? 'disabled' : '' }` }
          >
            <em>{ buildDescription(warning) }</em>
                &nbsp;(<strong>{ warning.code }</strong>)
          </li>
          )) }
        </ul>}
        </div>}
    </div>);
};

PiiValidationResults.propTypes = {
  validationCodes: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string,
    codeDescription: PropTypes.string
  })).isRequired,
  overrides: PropTypes.shape({
    current: PropTypes.shape({}),
    draft: PropTypes.shape({})
  }),
  headerName: PropTypes.string.isRequired,
  isOverriddenToInvalid: PropTypes.bool,
  formType: PropTypes.string.isRequired,
  piiValidationCodes: PropTypes.shape({
    formType: PropTypes.string,
    validationCodes: PropTypes.arrayOf(PropTypes.string)
  })
};

PiiValidationResults.defaultProps = {
  overrides: null,
  isOverriddenToInvalid: false,
  piiValidationCodes: null
};

const IndividualValidationResult = ({
  calculateValidity,
  showValidity,
  valid,
  rejections,
  warnings,
  overrides,
  formType,
  formId,
  customerId,
  customerName,
  isOnlineForm,
  isWithTreaty,
  isLocked,
  workflowStage,
  expiryDate,
  expiryReasonDesc,
  piiValidationCodes,
  isPiiProtectionEnabled,
  tinMatchResult,
  onCheckTin,
  showTinMatching,
  isImyChildResult,
  tinMatchFormProps,
  isFormDirty
}) => {
  const [activeSupportingDocsModal, setActiveSupportingDocsModal] = useState(null);

  const handleSupportingDocsModalOpen = (modalId) => {
    setActiveSupportingDocsModal(modalId);
  };

  const handleSupportingDocsModalClose = () => {
    setActiveSupportingDocsModal(null);
  };

  const instructions = isPiiProtectionEnabled === false ? warnings.filter(warning => (warning.code.indexOf('WRN') === -1))
    : warnings.filter(warning => (warning.code.indexOf('WRN') === -1) && !isPiiInstruction(formType, piiValidationCodes, warning.code, warnings, rejections));

  const showTinCheckStatus = () => {
    if (!tinMatchFormProps || !tinMatchFormProps.tin || !tinMatchFormProps.name) {
      return false;
    }
    const tinIsAppliedForOrEmpty = tinMatchFormProps.tin.appliedForTin || ((!tinMatchFormProps.tin.ssn && !tinMatchFormProps.tin.ein));

    const tinIsComplete = tinMatchFormProps.tin.ssn?.length === 9 || tinMatchFormProps.tin.ein?.length === 9;

    return formType.toLowerCase().startsWith('w9') && !isPiiProtectionEnabled && showTinMatching && !tinIsAppliedForOrEmpty && tinIsComplete;
  };
  const formFamily = formType && FormTypes[formType.toLowerCase()] && FormTypes[formType.toLowerCase()].getFamilyName();
  return (
  /* eslint-disable max-len, react/no-array-index-key */
    <div className="single-validation-result">
      <div className="result-header">
        <Link to={ `/forms/${ formType.toLowerCase() }/${ formId }` }>
          { (customerId != null && customerId.length > 0) ? `Customer ID: ${ customerId }` : '' }
          { (customerName != null && customerName.length > 0) ? ` ${ getDisplayTextForName(formType) } ${ customerName }` : '' }
        </Link>
      </div>
      {showValidity && isExpired(expiryDate) && isValid(calculateValidity, rejections, overrides, valid) && expiryDisplayText(expiryDate, expiryReasonDesc, formFamily) }
      {showValidity && !isExpired(expiryDate) && isValid(calculateValidity, rejections, overrides, valid) && <h2 className="valid">{validityDisplayText(true, isWithTreaty, isLocked, workflowStage, formFamily) } </h2> }
      {showValidity && !isValid(calculateValidity, rejections, overrides, valid) && <h2 className="invalid">{validityDisplayText(false, isWithTreaty, isLocked, workflowStage, formFamily) } </h2> }
      { !isExpired(expiryDate) && isValid(calculateValidity, rejections, overrides, valid) && <div className="valid-block">
        { !overriddenToInvalid(calculateValidity, overrides) && <span>The form is valid</span> }
        { isOnlineForm && (!warnings || warnings.length === 0) && <span> and does not have any warnings or rejections.</span> }
        { warnings && warnings.length !== 0 && !overriddenToInvalid(calculateValidity, overrides) && <span>, but it has some warnings / instructions.</span> }
        { warnings && warnings.length === 0 && !overriddenToInvalid(calculateValidity, overrides) && '.' }
      </div> }
      { showTinCheckStatus() && <TinCheckStatus
        onCheckTin={ onCheckTin }
        formId={ formId }
        tinMatchResult={ tinMatchResult }
        workflowStage={ workflowStage }
        isIndividualValidation
        isImyChildResult={ isImyChildResult }
        tinMatchFormProps={ tinMatchFormProps }
        isFormDirty={ isFormDirty }
      />}
      { ((rejections && rejections.length !== 0) || (rejections.length === 0 && overriddenToInvalid(calculateValidity, overrides))) && <div className="result-block">

        { formType.indexOf('IMY') === -1
          && <React.Fragment>
            <div className="heading-row">Rejections:</div>
            <div className="reasons-for-rejection-list">
              <ul>
                { overriddenToInvalid(calculateValidity, overrides) && <li>
                  <em>Client specific treatment, specified below.</em>
                  &nbsp;(<strong>REJ999999</strong>)
                </li> }
                { rejections.map(rejection => rejection != null && (isPiiProtectionEnabled === false
                  || (isPiiProtectionEnabled === true && !isPiiValidationCode(formType, piiValidationCodes, rejection.code))) && (
                  <li key={ rejection.key } className={ isOverridden(overrides, rejection.code) ? 'disabled' : '' }>
                    <em>{ buildDescription(rejection) }</em>
                    &nbsp;(<strong>{ rejection.code }</strong>)
                    { rejection.clearingDocuments?.length > 0 && <SupportingDocumentCures
                      modalId={ rejection.code }
                      supportingDocuments={ rejection.clearingDocuments }
                      onModalOpen={ handleSupportingDocsModalOpen }
                      onModalClose={ handleSupportingDocsModalClose }
                      activeModal={ activeSupportingDocsModal }
                    /> }
                  </li>
                )) }
              </ul>
            </div>
            { isPiiProtectionEnabled === true && <PiiValidationResults
              isOverriddenToInvalid={ overriddenToInvalid(calculateValidity, overrides) }
              overrides={ overrides }
              validationCodes={ rejections }
              headerName="Rejections"
              formType={ formType }
              piiValidationCodes={ piiValidationCodes }
            /> }
            </React.Fragment> }
        { formType.indexOf('IMY') > -1 && <React.Fragment>
          { rejections.some(rejection => (rejection.code.substring(3) < 500000 || (rejection.code.substring(3) >= MANUAL_REJECTION_MIN_CODE && rejection.code.substring(3) <= MANUAL_REJECTION_MAX_CODE)) && rejection.code.substring(3) !== 999999)
          && <React.Fragment>
            <div className="heading-row">Form Rejections:</div>
            <div className="reasons-for-rejection-list">
              <ul>
                { overriddenToInvalid(calculateValidity, overrides) && <li>
                  <em>Client specific treatment, specified below.</em>
                  &nbsp;(<strong>REJ999999</strong>)
                </li> }
                {rejections.map(rejection => rejection != null && (rejection.code.substring(3) < 500000 || (rejection.code.substring(3) >= MANUAL_REJECTION_MIN_CODE && rejection.code.substring(3) <= MANUAL_REJECTION_MAX_CODE)) && (isPiiProtectionEnabled === false
                    || (isPiiProtectionEnabled === true && !isPiiValidationCode(formType, piiValidationCodes, rejection.code))) && (
                    <li key={ rejection.key } className={ isOverridden(overrides, rejection.code) ? 'disabled' : '' }>
                      <em>{ buildDescription(rejection) }</em>
                      &nbsp;(<strong>{ rejection.code }</strong>)
                      { rejection.clearingDocuments?.length > 0 && <SupportingDocumentCures
                        modalId={ rejection.code }
                        supportingDocuments={ rejection.clearingDocuments }
                        onModalOpen={ handleSupportingDocsModalOpen }
                        onModalClose={ handleSupportingDocsModalClose }
                        activeModal={ activeSupportingDocsModal }
                      /> }
                    </li>
                )) }
              </ul>
              {isPiiProtectionEnabled === true && <PiiValidationResults
                isOverriddenToInvalid={ overriddenToInvalid(calculateValidity, overrides) }
                overrides={ overrides }
                validationCodes={ rejections }
                headerName="Rejections"
                formType={ formType }
                piiValidationCodes={ piiValidationCodes }
              />}
            </div>
          </React.Fragment> }
          { rejections.some(rejection => rejection.code.substring(3) >= 500000 && rejection.code.substring(3) < 999999 && !(rejection.code.substring(3) >= MANUAL_REJECTION_MIN_CODE && rejection.code.substring(3) <= MANUAL_REJECTION_MAX_CODE))
          && <React.Fragment>
            <br />
            <div className="heading-row">IMY Structure Rejections:</div>
            <div className="reasons-for-rejection-list">
              <ul>
                {rejections.map(rejection => rejection != null && rejection.code.substring(3) >= 500000 && rejection.code.substring(3) < 999999 && !(rejection.code.substring(3) >= MANUAL_REJECTION_MIN_CODE && rejection.code.substring(3) <= MANUAL_REJECTION_MAX_CODE)
                  && (isPiiProtectionEnabled === false || (isPiiProtectionEnabled === true && !isPiiValidationCode(formType, piiValidationCodes, rejection.code)))
                  && (<li key={ rejection.key } className={ isOverridden(overrides, rejection.code) ? 'disabled' : '' }>
                    <em>{ buildDescription(rejection) }</em>
                    &nbsp;(<strong>{ rejection.code }</strong>)
                    { rejection.clearingDocuments?.length > 0 && <SupportingDocumentCures
                      modalId={ rejection.code }
                      supportingDocuments={ rejection.clearingDocuments }
                      onModalOpen={ handleSupportingDocsModalOpen }
                      onModalClose={ handleSupportingDocsModalClose }
                      activeModal={ activeSupportingDocsModal }
                    /> }
                  </li>
                  )) }
              </ul>
              {isPiiProtectionEnabled === true && <PiiValidationResults
                isOverriddenToInvalid={ overriddenToInvalid(calculateValidity, overrides) }
                overrides={ overrides }
                validationCodes={ rejections }
                headerName="Rejections"
                formType={ formType }
                piiValidationCodes={ piiValidationCodes }
              />}
            </div>
          </React.Fragment> }
        </React.Fragment> }
      </div> }
      { ((warnings && warnings.length !== 0) || (warnings.length === 0 && overriddenToInvalid(calculateValidity, overrides))) && <div className="result-block">
        { isOnlineForm && warnings.filter(w => w.code.indexOf('WRN') > -1).length > 0 && <div className="heading-row" style={ { fontWeight: 'bold', marginBottom: '1rem' } }>Warnings:</div> }
        { !isOnlineForm && warnings.filter(w => w.code.indexOf('WRN') > -1).length > 0 && <div className="heading-row">Warnings:</div> }
        <div className="red-flags-and-recs-list">
          <ul>
            { overriddenToInvalid(calculateValidity, overrides) && <li className="red-flag">
              <em>{ overriddenToInvalidText(overrides) }</em>
              &nbsp;(<strong>WRN999999</strong>)
            </li> }
            { warnings.filter(w => w.code.indexOf('WRN') > -1).map(warning => warning != null && (isPiiProtectionEnabled === false
              || (isPiiProtectionEnabled === true && !isPiiValidationCode(formType, piiValidationCodes, warning.code))) && (
              <li
                key={ warning.key }
                className={ `red-flag ${ hasOverriddenRejection(warning.code, rejections, overrides) ? 'disabled' : '' }` }
              >
                <em>{ buildDescription(warning) }</em>
                &nbsp;(<strong>{ warning.code }</strong>)
                { warning.clearingDocuments?.length > 0 && <SupportingDocumentCures
                  modalId={ warning.code }
                  supportingDocuments={ warning.clearingDocuments }
                  onModalOpen={ handleSupportingDocsModalOpen }
                  onModalClose={ handleSupportingDocsModalClose }
                  activeModal={ activeSupportingDocsModal }
                /> }
              </li>
            )) }
          </ul>
          { isPiiProtectionEnabled === true && <PiiValidationResults
            isOverriddenToInvalid={ overriddenToInvalid(calculateValidity, overrides) }
            overrides={ overrides }
            validationCodes={ warnings.filter(w => w.code.indexOf('WRN') > -1) }
            headerName="Warnings"
            formType={ formType }
            piiValidationCodes={ piiValidationCodes }
          /> }
        </div>
      </div> }

      {((isPiiProtectionEnabled === false
        || (isPiiProtectionEnabled === true && (instructions && instructions.length > 0)))
        && ((warnings && warnings.length !== 0) || (warnings.length === 0 && overriddenToInvalid(calculateValidity, overrides)))) && <div className="result-block">
          { isOnlineForm && <div className="heading-row" style={ { fontWeight: 'bold', marginBottom: '1rem' } }>Instructions:</div> }
          { !isOnlineForm && <div className="heading-row">Instructions:</div> }
          <div className="red-flags-and-recs-list">
            <ul>
              {instructions.map(instruction => instruction != null
            && (
              <li
                key={ instruction.key }
                className={ `recommendation ${ hasOverriddenRejection(instruction.code, rejections, overrides) ? 'disabled' : '' }` }
              >
                <em>{buildDescription(instruction) }</em>
                  &nbsp;(<strong>{instruction.code }</strong>)
              </li>
            )) }
            </ul>
          </div>
        </div> }
    </div>
    /* eslint-enable max-len, react/no-array-index-key */
  );
};

IndividualValidationResult.propTypes = {
  valid: PropTypes.bool.isRequired,
  rejections: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string,
    codeDescription: PropTypes.string
  })).isRequired,
  warnings: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  overrides: PropTypes.shape({
    current: PropTypes.shape({}),
    draft: PropTypes.shape({})
  }),
  formType: PropTypes.string.isRequired,
  formId: PropTypes.number.isRequired,
  customerId: PropTypes.string,
  customerName: PropTypes.string,
  isOnlineForm: PropTypes.bool.isRequired,
  calculateValidity: PropTypes.bool,
  showValidity: PropTypes.bool,
  isWithTreaty: PropTypes.bool,
  isLocked: PropTypes.bool,
  workflowStage: PropTypes.number,
  expiryDate: PropTypes.string,
  expiryReasonDesc: PropTypes.string,
  piiValidationCodes: PropTypes.shape({
    formType: PropTypes.string,
    validationCodes: PropTypes.arrayOf(PropTypes.string)
  }),
  isPiiProtectionEnabled: PropTypes.bool,
  tinMatchResult: PropTypes.arrayOf(PropTypes.shape()),
  onCheckTin: PropTypes.func,
  showTinMatching: PropTypes.bool,
  isImyChildResult: PropTypes.bool,
  tinMatchFormProps: PropTypes.shape(),
  isFormDirty: PropTypes.bool
};

IndividualValidationResult.defaultProps = {
  customerId: '',
  customerName: '',
  overrides: null,
  calculateValidity: false,
  showValidity: true,
  isWithTreaty: false,
  isLocked: false,
  workflowStage: 0,
  expiryDate: null,
  expiryReasonDesc: '',
  piiValidationCodes: null,
  isPiiProtectionEnabled: false,
  tinMatchResult: null,
  onCheckTin: null,
  showTinMatching: false,
  isImyChildResult: false,
  tinMatchFormProps: null,
  isFormDirty: false
};

export default IndividualValidationResult;
